import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { teamApi, PromptConfig } from '@/services/api';
import { getErrorMessage } from '@/utils/errors';
import TeamHeader from '@/components/team/TeamHeader';

const TAB_ROUTES = [
  { label: 'Overview', path: 'overview' },
  { label: 'Context', path: 'knowledge' },
  { label: 'Chatbot', path: 'chatbot' },
  { label: 'Agent', path: 'agent', featureFlag: 'enable_agent' as const },
  { label: 'Settings', path: 'settings' },
];

export default function TeamHubPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: team, isLoading: teamLoading } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamApi.get(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: promptConfig } = useQuery({
    queryKey: ['team-prompt-config', teamId],
    queryFn: () => teamApi.getPromptConfig(teamId!).then((res) => res.data) as Promise<PromptConfig>,
    enabled: !!teamId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => teamApi.delete(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      navigate('/teams');
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Failed to delete team: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  // Determine active tab from URL
  const currentPath = location.pathname.split('/').pop() || 'overview';
  const visibleTabs = TAB_ROUTES.filter(tab => {
    if (tab.featureFlag) {
      if (!promptConfig) return true; // show all tabs while loading to avoid flicker
      return promptConfig[tab.featureFlag];
    }
    return true;
  });
  const activeTabIndex = Math.max(0, visibleTabs.findIndex(tab => tab.path === currentPath));

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = visibleTabs[newValue];
    if (tab) {
      navigate(`/teams/${teamId}/${tab.path}`);
    }
  };

  if (teamLoading) {
    return <Skeleton variant="rectangular" height={400} />;
  }

  if (!team) {
    return <Alert severity="error">Team not found</Alert>;
  }

  return (
    <Box>
      <TeamHeader
        team={team}
        onDelete={() => setDeleteDialogOpen(true)}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {visibleTabs.map((tab) => (
            <Tab key={tab.path} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      <Outlet context={{ teamId, team, promptConfig, snackbar, setSnackbar }} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the team "{team?.name}"? This action will deactivate the team.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
